import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import UpdateNotesTable from "./UpdateNotesTable";
import { fetchAllSubjectsSimple } from "../pages/services/courseService";
import "../styles/AddForm.css";
import "../styles/UpdateBatch.css";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const API_URL = process.env.REACT_APP_API_URL;

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const UpdateBatch = () => {
  const { id: batchId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    batchTitle: "", batchDuration: "", batchDescription: "",
    trainerId: "", batchStartTime: "", batchEndTime: "",
    batchDays: [], batchStatus: "", isActive: true, batchStartDate: "", batchEndDate: "",
    courseId: "", onlineClassLink: "", trainerLockEnabled: false,
    subjects: [],
  });

  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [addSubjectId, setAddSubjectId] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showUpdateNotesDialog, setShowUpdateNotesDialog] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchBatch = async (subjectsList) => {
      try {
        const res = await fetch(`${API_URL}/api/batches/${batchId}`);
        const response = await res.json();
        if (!res.ok || !response?.data) {
          navigate("/not-found", { replace: true });
          return;
        }
        const [startTime, endTime] =
          typeof response.data.batchTime === "string" && response.data.batchTime.includes(" -")
            ? response.data.batchTime.split(" - ")
            : ["", ""];

        const formatDate = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");
        const titleById = new Map(subjectsList.map((s) => [s._id, s.title]));

        setFormData({
          batchTitle: response.data.batchTitle || "",
          batchDuration: response.data.batchDuration || "",
          batchDescription: response.data.batchDescription || "",
          trainerId: response.data.trainer?._id || "",
          batchStartTime: startTime,
          batchEndTime: endTime,
          batchDays: response.data.batchDays || [],
          batchStatus: response.data.batchStatus || "",
          isActive: response.data.isActive !== false,
          batchStartDate: formatDate(response.data.batchStartDate),
          batchEndDate: formatDate(response.data.batchEndDate),
          courseId: response.data.courseId || "",
          onlineClassLink: response.data.onlineClassLink || "",
          trainerLockEnabled: response.data.trainerLockEnabled || false,
          subjects: (response.data.subjects || [])
            .filter((s) => s.subjectId)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((s) => {
              const subjectId = s.subjectId._id?.toString() || s.subjectId.toString();
              return { subjectId, title: titleById.get(subjectId) || "Unknown Subject", order: s.order };
            }),
        });
      } catch (err) {
        console.error("Error loading batch:", err);
      }
    };

    const fetchTrainers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/trainers`);
        const response = await res.json();
        setTrainers(response || []);
      } catch (err) {
        console.error("Error loading trainers:", err);
      }
    };

    const fetchCoursesList = async () => {
      try {
        const res = await fetch(`${API_URL}/api/courses?pageSize=200`);
        const response = await res.json();
        setCourses(response.data || []);
      } catch (err) {
        console.error("Error loading courses:", err);
      }
    };

    fetchAllSubjectsSimple()
      .then((subjectsList) => {
        setAvailableSubjects(subjectsList);
        fetchBatch(subjectsList);
      })
      .catch(() => {
        setAvailableSubjects([]);
        fetchBatch([]);
      });
    fetchTrainers();
    fetchCoursesList();
  }, [batchId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (name === "batchStartTime" || name === "batchEndTime") {
      setErrors(p => { const n = { ...p }; delete n.batchTime; return n; });
    } else {
      setErrors(p => ({ ...p, [name]: "" }));
    }
  };

  const toggleDay = (day) => {
    setFormData(p => ({
      ...p,
      batchDays: p.batchDays.includes(day)
        ? p.batchDays.filter(d => d !== day)
        : [...p.batchDays, day],
    }));
    setErrors(p => ({ ...p, batchDays: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.batchTitle.trim())       e.batchTitle       = "Batch title is required";
    if (!formData.batchDuration.trim())    e.batchDuration    = "Duration is required";
    if (!formData.batchDescription.trim()) e.batchDescription = "Description is required";
    if (!formData.trainerId)               e.trainerId        = "Trainer is required";
    if (!formData.batchStartTime || !formData.batchEndTime) e.batchTime = "Both start and end time are required";
    if (formData.batchDays.length === 0)   e.batchDays        = "Select at least one day";
    if (!formData.batchStatus.trim())      e.batchStatus      = "Status is required";
    if (formData.batchStartDate && formData.batchEndDate && formData.batchEndDate < formData.batchStartDate)
      e.batchEndDate = "End date cannot be before start date";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const batchTime = `${formData.batchStartTime} - ${formData.batchEndTime}`;
      const payload = {
        ...formData,
        batchTime,
        courseId: formData.courseId || null,
        subjects: formData.subjects.map((s, i) => ({ subjectId: s.subjectId, order: i })),
      };
      delete payload.batchStartTime;
      delete payload.batchEndTime;

      const response = await fetch(`${API_URL}/api/batches/${batchId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to update batch");
      }

      navigate("/batches", { state: { successMessage: "Batch updated successfully!" } });
    } catch (err) {
      setErrorMessage(err.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const notesRes = await fetch(`${API_URL}/api/notes/${batchId}`);
      if (!notesRes.ok) throw new Error("Failed to fetch notes");
      const notesData = await notesRes.json();
      for (const note of (notesData.notes || [])) {
        await fetch(`${API_URL}/api/notes/${note._id}`, { method: "DELETE" });
      }
      const batchDeleteRes = await fetch(`${API_URL}/api/batches/${batchId}`, { method: "DELETE" });
      if (!batchDeleteRes.ok) throw new Error("Failed to delete batch");
      navigate("/batches", { state: { successMessage: "Batch deleted successfully!" } });
    } catch (error) {
      console.error("Error deleting batch or notes:", error);
      alert("Error deleting batch or notes: " + error.message);
    }
  };

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving changes…</p>
        </div>
      )}

      {showDeletePopup && (
        <div className="uf-delete-overlay">
          <div className="uf-delete-modal">
            <p className="uf-delete-msg">Are you sure you want to delete this batch?</p>
            <div className="uf-delete-actions">
              <button className="uf-cancel-btn" onClick={() => setShowDeletePopup(false)}>Cancel</button>
              <button className="uf-confirm-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon green">📚</div>
          <div className="af-header-text" style={{ flex: 1 }}>
            <h2 className="af-title">Update Batch</h2>
            <p className="af-subtitle">Edit batch details, schedule, and notes</p>
          </div>
          <button type="button" className="uf-delete-trigger" onClick={() => setShowDeletePopup(true)}>
            Delete
          </button>
        </div>

        {successMessage && <div className="af-alert af-alert-success" style={{margin:"16px 32px 0"}}>{successMessage}</div>}
        {errorMessage   && <div className="af-alert af-alert-error"   style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            <div className="af-section">
              <p className="af-section-title">Batch Information</p>
              <Field label="Batch Title" required error={errors.batchTitle}>
                <input type="text" name="batchTitle" placeholder="e.g. MERN Stack – Batch 12"
                  value={formData.batchTitle} onChange={handleChange}
                  className={errors.batchTitle ? "error-input" : ""} />
              </Field>
              <div className="af-row">
                <Field label="Duration" required error={errors.batchDuration}>
                  <input type="text" name="batchDuration" placeholder="e.g. 3 months"
                    value={formData.batchDuration} onChange={handleChange}
                    className={errors.batchDuration ? "error-input" : ""} />
                </Field>
                <Field label="Trainer" required error={errors.trainerId}>
                  <select name="trainerId" value={formData.trainerId} onChange={handleChange}
                    className={errors.trainerId ? "error-input" : ""}>
                    <option value="">{trainers.length === 0 ? "Loading…" : "Select trainer"}</option>
                    {trainers.map(t => <option key={t._id} value={t._id}>{t.trainerName}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Description" required error={errors.batchDescription}>
                <textarea name="batchDescription" rows={3}
                  placeholder="Brief overview of what this batch covers…"
                  value={formData.batchDescription} onChange={handleChange}
                  className={errors.batchDescription ? "error-input" : ""} />
              </Field>
            </div>

            <div className="af-section">
              <p className="af-section-title">Schedule</p>
              <div className="af-field">
                <label className="af-label">Class Time<span className="af-required">*</span></label>
                <div className="af-time-row">
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.5px"}}>From</label>
                    <input type="time" name="batchStartTime" value={formData.batchStartTime}
                      onChange={handleChange} className={errors.batchTime ? "error-input" : ""} />
                  </div>
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.5px"}}>To</label>
                    <input type="time" name="batchEndTime" value={formData.batchEndTime}
                      onChange={handleChange} className={errors.batchTime ? "error-input" : ""} />
                  </div>
                </div>
                {errors.batchTime && <span className="af-error">{errors.batchTime}</span>}
              </div>

              <div className="af-field">
                <label className="af-label">Batch Days<span className="af-required">*</span></label>
                <div className="af-day-grid">
                  {DAYS.map(day => (
                    <span key={day}
                      className={`af-day-chip${formData.batchDays.includes(day) ? " selected" : ""}`}
                      onClick={() => toggleDay(day)}>
                      {day.slice(0, 3)}
                    </span>
                  ))}
                </div>
                {errors.batchDays && <span className="af-error">{errors.batchDays}</span>}
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Online Class &amp; Settings</p>
              <Field label="Online Class Link">
                <input type="url" name="onlineClassLink"
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  value={formData.onlineClassLink} onChange={handleChange} />
              </Field>
              <div className="af-field">
                <label className="af-label" style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                  <input type="checkbox" name="trainerLockEnabled"
                    checked={formData.trainerLockEnabled}
                    onChange={e => setFormData(p => ({ ...p, trainerLockEnabled: e.target.checked }))}
                    style={{width:16,height:16}} />
                  Enable Trainer Lock — topics only unlock for students after trainer teaches them
                </label>
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Course Association</p>
              <Field label="Linked Course">
                <select name="courseId" value={formData.courseId} onChange={handleChange}>
                  <option value="">None — not linked to a course</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.courseTitle}</option>)}
                </select>
              </Field>
            </div>

            <div className="af-section">
              <p className="af-section-title">Subjects</p>
              <Field label="Add Subject" hint="Subjects shown to this batch's students under Notes — managed per batch, not per course">
                <select
                  value={addSubjectId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAddSubjectId(val);
                    if (!val) return;
                    const subj = availableSubjects.find((s) => s._id === val);
                    if (!subj) { setAddSubjectId(""); return; }
                    if (formData.subjects.some((s) => s.subjectId === val)) { setAddSubjectId(""); return; }
                    const order = formData.subjects.length;
                    setFormData((p) => ({
                      ...p,
                      subjects: [...p.subjects, { subjectId: val, title: subj.title, order }],
                    }));
                    setAddSubjectId("");
                  }}
                >
                  <option value="">— Select a subject to add —</option>
                  {availableSubjects
                    .filter((s) => !formData.subjects.some((sel) => sel.subjectId === s._id))
                    .map((s) => (
                      <option key={s._id} value={s._id}>{s.title}</option>
                    ))}
                </select>
              </Field>

              {formData.subjects.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                  {formData.subjects.map((s, i) => (
                    <span
                      key={s.subjectId}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "#ede9fe", color: "#5b21b6",
                        borderRadius: 20, padding: "5px 14px 5px 10px",
                        fontSize: 13, fontWeight: 600, border: "1px solid #c4b5fd",
                      }}
                    >
                      <span style={{ fontSize: 11, color: "#7c3aed", minWidth: 18 }}>#{i + 1}</span>
                      {s.title}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            subjects: p.subjects
                              .filter((_, idx) => idx !== i)
                              .map((sub, idx) => ({ ...sub, order: idx })),
                          }))
                        }
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "#7c3aed", fontSize: 16, padding: "0 0 0 4px",
                          lineHeight: 1, fontWeight: 700,
                        }}
                        title="Remove subject"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {formData.subjects.length === 0 && (
                <p style={{ fontSize: 13, color: "#9ca3af", margin: "6px 0 0" }}>No subjects added yet. Select from the dropdown above.</p>
              )}
            </div>

            <div className="af-section">
              <p className="af-section-title">Status & Dates</p>
              <Field label="Batch Status" required error={errors.batchStatus}>
                <select name="batchStatus" value={formData.batchStatus} onChange={handleChange}
                  className={errors.batchStatus ? "error-input" : ""}>
                  <option value="">Select status…</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Completed">Completed</option>
                  <option value="Deleted">Deleted</option>
                </select>
              </Field>
                <Field label="Visibility" error={errors.isActive}>
                  <label className="af-switch">
                    <input
                      type="checkbox"
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                    />
                    <span>{formData.isActive !== false ? "Active" : "Inactive"}</span>
                  </label>
                  <span className="af-hint">
                    Inactive batches are hidden from lists and filter dropdowns across
                    the Admin and Trainer apps. Nothing is deleted, and enrolled students keep access to theirs.
                  </span>
                </Field>
              <div className="af-row">
                <Field label="Start Date" error={errors.batchStartDate}>
                  <input type="date" name="batchStartDate" value={formData.batchStartDate}
                    onChange={handleChange} />
                </Field>
                <Field label="End Date" error={errors.batchEndDate}>
                  <input type="date" name="batchEndDate" value={formData.batchEndDate}
                    onChange={handleChange} />
                </Field>
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Notes (Optional)</p>
              <div className="af-action-strip">
                <button type="button" className="af-secondary-btn"
                  onClick={() => setShowUpdateNotesDialog(true)}>
                  + Add Notes
                </button>
              </div>
              <UpdateNotesTable
                batchId={batchId}
                openUpdateNotesDialogExternally={showUpdateNotesDialog}
                setOpenUpdateNotesDialogExternally={setShowUpdateNotesDialog}
              />
            </div>
          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Saving…" : "Update Batch"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateBatch;
